import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const FavoritesContext = createContext();

const MAX_FAVORITES = 10;
const STORAGE_KEY = 'hippocrate_favorite_scores';

// Helper to load from localStorage
const loadFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.slice(0, MAX_FAVORITES) : [];
    }
  } catch (error) {
    // localStorage indisponible/corrompu (mode privé, quota, JSON invalide) → repli sur liste vide
  }
  return [];
};

export const FavoritesProvider = ({ children }) => {
  // Initialize state directly from localStorage to avoid race condition
  const [favorites, setFavorites] = useState(() => loadFromStorage());
  const isInitialized = useRef(false);

  // Mark as initialized after first render
  useEffect(() => {
    isInitialized.current = true;
  }, []);

  // Save to localStorage whenever favorites change (but not on initial load)
  useEffect(() => {
    if (isInitialized.current) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
      } catch (error) {
        // Écriture localStorage impossible (mode privé/quota) → favoris non persistés, sans blocage de l'UI
      }
    }
  }, [favorites]);

  const addFavorite = (score) => {
    if (favorites.length >= MAX_FAVORITES) {
      return { success: false, message: `Maximum ${MAX_FAVORITES} favoris atteint` };
    }
    if (favorites.some(f => f.id === score.id)) {
      return { success: false, message: 'Ce score est déjà dans vos favoris' };
    }
    
    // Store only essential data
    const favoriteData = {
      id: score.id,
      name: score.name,
      fullName: score.fullName,
      description: score.description,
      categoryKey: score.categoryKey,
      categoryName: score.categoryName,
      cotation: score.cotation
    };
    
    setFavorites(prev => [...prev, favoriteData]);
    return { success: true, message: 'Score ajouté aux favoris' };
  };

  const removeFavorite = (scoreId) => {
    setFavorites(prev => prev.filter(f => f.id !== scoreId));
    return { success: true, message: 'Score retiré des favoris' };
  };

  const toggleFavorite = (score) => {
    if (isFavorite(score.id)) {
      return removeFavorite(score.id);
    } else {
      return addFavorite(score);
    }
  };

  const isFavorite = (scoreId) => {
    return favorites.some(f => f.id === scoreId);
  };

  const clearAllFavorites = () => {
    setFavorites([]);
  };

  return (
    <FavoritesContext.Provider value={{
      favorites,
      addFavorite,
      removeFavorite,
      toggleFavorite,
      isFavorite,
      clearAllFavorites,
      maxFavorites: MAX_FAVORITES,
      remainingSlots: MAX_FAVORITES - favorites.length
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export default FavoritesContext;
